import { useShowToast } from "@meiden-connect/ui";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";
import {
  useCanvasApi,
  type AllCanvasItem,
  type FavoriteCanvasItem,
  type RecentCanvasItem,
  type TrashedCanvasItem,
} from "../../lib/canvas-api";

export interface DashboardCanvasActionTarget {
  readonly canvasId: string;
  readonly title: string;
  readonly thumbnailKey?: string | undefined;
  readonly thumbnailUrl?: string | undefined;
  readonly updatedAt: string;
}

type RecentData = { items: readonly RecentCanvasItem[] };
type FavoriteData = { items: readonly FavoriteCanvasItem[] };
type AllData = { items: readonly AllCanvasItem[] };
type TrashData = { items: readonly TrashedCanvasItem[] };

const QUERY_KEYS = {
  recent: ["recent-canvases"] as const,
  favorites: ["favorites"] as const,
  all: ["all-canvases"] as const,
  trash: ["trash"] as const,
};

interface CacheSnapshot {
  readonly recent: RecentData | undefined;
  readonly favorites: FavoriteData | undefined;
  readonly all: AllData | undefined;
  readonly trash: TrashData | undefined;
}

export function useDashboardCanvasActions(): {
  readonly busy: boolean;
  readonly addFavorite: (target: DashboardCanvasActionTarget) => Promise<void>;
  readonly removeFavorite: (target: DashboardCanvasActionTarget) => Promise<void>;
  readonly trash: (target: DashboardCanvasActionTarget) => Promise<void>;
  readonly restore: (target: DashboardCanvasActionTarget) => Promise<void>;
} {
  const api = useCanvasApi();
  const queryClient = useQueryClient();
  const showToast = useShowToast();
  const busyRef = useRef(false);
  const [busy, setBusy] = useState(false);

  const execute = useCallback(
    async (
      request: () => Promise<void>,
      optimisticUpdate: (now: string) => void,
      successMessage: string,
      onSuccess: () => Promise<void>,
    ): Promise<void> => {
      if (busyRef.current) return;
      busyRef.current = true;
      setBusy(true);
      let snapshot: CacheSnapshot | undefined;
      try {
        snapshot = await captureSnapshot(queryClient);
        optimisticUpdate(new Date().toISOString());
        await request();
        await onSuccess();
        showToast("success", successMessage);
      } catch (error) {
        if (snapshot) restoreSnapshot(queryClient, snapshot);
        showToast("warning", toErrorMessage(error));
      } finally {
        busyRef.current = false;
        setBusy(false);
      }
    },
    [queryClient, showToast],
  );

  const addFavorite = useCallback(
    async (target: DashboardCanvasActionTarget): Promise<void> =>
      execute(
        () => api.addFavorite(target.canvasId),
        (now) => {
          queryClient.setQueryData<FavoriteData>(QUERY_KEYS.favorites, (current) => ({
            items: [
              {
                ...target,
                updatedAt: target.updatedAt,
                favoritedAt: now,
              },
              ...(current?.items.filter((item) => item.canvasId !== target.canvasId) ?? []),
            ],
          }));
        },
        "お気に入りに追加しました。",
        () => markAllStale(queryClient),
      ),
    [api, execute, queryClient],
  );

  const removeFavorite = useCallback(
    async (target: DashboardCanvasActionTarget): Promise<void> =>
      execute(
        () => api.removeFavorite(target.canvasId),
        () => {
          queryClient.setQueryData<FavoriteData>(QUERY_KEYS.favorites, (current) => ({
            items: current?.items.filter((item) => item.canvasId !== target.canvasId) ?? [],
          }));
        },
        "お気に入りから外しました。",
        () => markAllStale(queryClient),
      ),
    [api, execute, queryClient],
  );

  const trash = useCallback(
    async (target: DashboardCanvasActionTarget): Promise<void> =>
      execute(
        () => api.trashCanvas(target.canvasId),
        (now) => {
          removeFromRegularLists(queryClient, target.canvasId);
          queryClient.setQueryData<TrashData>(QUERY_KEYS.trash, (current) => ({
            items: [
              { ...target, trashedAt: now },
              ...(current?.items.filter((item) => item.canvasId !== target.canvasId) ?? []),
            ],
          }));
        },
        "キャンバスをゴミ箱へ移動しました。",
        () => markAllStale(queryClient),
      ),
    [api, execute, queryClient],
  );

  const restore = useCallback(
    async (target: DashboardCanvasActionTarget): Promise<void> =>
      execute(
        () => api.restoreCanvas(target.canvasId),
        () => {
          queryClient.setQueryData<TrashData>(QUERY_KEYS.trash, (current) => ({
            items: current?.items.filter((item) => item.canvasId !== target.canvasId) ?? [],
          }));
        },
        "キャンバスを元に戻しました。",
        async () => {
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.trash, refetchType: "none" }),
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.recent }),
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.favorites }),
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all }),
          ]);
        },
      ),
    [api, execute, queryClient],
  );

  return { busy, addFavorite, removeFavorite, trash, restore };
}

async function captureSnapshot(queryClient: QueryClient): Promise<CacheSnapshot> {
  await Promise.all(Object.values(QUERY_KEYS).map((queryKey) => queryClient.cancelQueries({ queryKey })));
  return {
    recent: queryClient.getQueryData<RecentData>(QUERY_KEYS.recent),
    favorites: queryClient.getQueryData<FavoriteData>(QUERY_KEYS.favorites),
    all: queryClient.getQueryData<AllData>(QUERY_KEYS.all),
    trash: queryClient.getQueryData<TrashData>(QUERY_KEYS.trash),
  };
}

function restoreSnapshot(queryClient: QueryClient, snapshot: CacheSnapshot): void {
  queryClient.setQueryData(QUERY_KEYS.recent, snapshot.recent);
  queryClient.setQueryData(QUERY_KEYS.favorites, snapshot.favorites);
  queryClient.setQueryData(QUERY_KEYS.all, snapshot.all);
  queryClient.setQueryData(QUERY_KEYS.trash, snapshot.trash);
}

function removeFromRegularLists(queryClient: QueryClient, canvasId: string): void {
  queryClient.setQueryData<RecentData>(QUERY_KEYS.recent, (current) => ({
    items: current?.items.filter((item) => item.canvasId !== canvasId) ?? [],
  }));
  queryClient.setQueryData<FavoriteData>(QUERY_KEYS.favorites, (current) => ({
    items: current?.items.filter((item) => item.canvasId !== canvasId) ?? [],
  }));
  queryClient.setQueryData<AllData>(QUERY_KEYS.all, (current) => ({
    items: current?.items.filter((item) => item.canvasId !== canvasId) ?? [],
  }));
}

async function markAllStale(queryClient: QueryClient): Promise<void> {
  await Promise.all(
    Object.values(QUERY_KEYS).map((queryKey) =>
      queryClient.invalidateQueries({ queryKey, refetchType: "none" }),
    ),
  );
}

function toErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "キャンバスの操作に失敗しました。";
}
